/**
 * Application entry point and game controller.
 *
 * Owns the main-thread state: the latest worker snapshot, selection, view
 * options (flip), animation, and the EngineClient RPC wrapper. All rules and
 * AI live in the worker; this file only orchestrates messages and rendering.
 */

import type {
  GameOverInfo,
  Pos,
  SearchBudget,
  SearchInfoMessage,
  StateSnapshot,
  Strength,
  WorkerRequest,
  WorkerResponse,
} from './protocol/messages';
import { cellSide, responseKind, validateSnapshot } from './protocol/messages';
import { animatePoint, animateTicks } from './ui/animations';
import { BoardRenderer, cellIndex, type BoardView } from './ui/boardRenderer';
import { cellCenter, posFromPoint } from './ui/coordinates';
import { attachBoardHover, attachBoardInput } from './ui/input';
import { Panels, type AboutInfo, type NewGameOptions } from './ui/panels';
import { SoundPlayer } from './ui/sounds';

// -- EngineClient: promise-based RPC over the worker -------------------------

class EngineClient {
  private worker: Worker;
  private seq = 0;
  private pending = new Map<number, (msg: WorkerResponse) => void>();
  onProgress: (stage: string, detail?: string) => void = () => undefined;
  onSearchInfo: (info: SearchInfoMessage) => void = () => undefined;
  onFatalError: (message: string) => void = () => undefined;

  constructor() {
    this.worker = new Worker(new URL('./worker/engineWorker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent) => this.dispatch(event.data);
    this.worker.onerror = (event) => {
      this.onFatalError(event.message ?? 'engine worker crashed');
    };
  }

  private dispatch(raw: unknown): void {
    let kind: WorkerResponse['type'];
    try {
      kind = responseKind(raw);
    } catch {
      return; // unparseable message — never crash the UI on worker noise
    }
    if (kind === 'init_progress') {
      const progress = raw as { stage: string; detail?: string };
      this.onProgress(progress.stage, progress.detail);
      return;
    }
    if (kind === 'search_info') {
      this.onSearchInfo(raw as SearchInfoMessage);
      return;
    }
    const msg = raw as WorkerResponse & { id?: number };
    if (kind === 'error' && (msg as { fatal?: boolean }).fatal === true && msg.id === undefined) {
      this.onFatalError((msg as { message?: string }).message ?? 'fatal worker error');
      return;
    }
    if (msg.id !== undefined) {
      const resolve = this.pending.get(msg.id);
      if (resolve) {
        this.pending.delete(msg.id);
        resolve(msg);
      }
    }
  }

  private call<T extends WorkerResponse>(msg: WorkerRequest): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve) => {
      this.pending.set(id, (response) => resolve(response as T));
      this.worker.postMessage({ ...msg, id });
    });
  }

  init(): Promise<WorkerResponse> {
    const base = document.baseURI;
    return this.call<WorkerResponse>({
      type: 'init',
      engineZipUrl: new URL('engine/engine.zip', base).href,
      manifestUrl: new URL('engine/manifest.json', base).href,
      pyodideBaseUrl: new URL('pyodide/', base).href,
    });
  }

  newGame(options: NewGameOptions & { budget?: SearchBudget }): Promise<WorkerResponse> {
    return this.call<WorkerResponse>({ type: 'new_game', ...options });
  }

  humanMove(from: Pos, to: Pos): Promise<WorkerResponse> {
    return this.call<WorkerResponse>({ type: 'human_move', from, to });
  }

  requestAiMove(): Promise<WorkerResponse> {
    return this.call<WorkerResponse>({ type: 'request_ai_move' });
  }

  undo(): Promise<WorkerResponse> {
    return this.call<WorkerResponse>({ type: 'undo' });
  }

  setStrength(strength: Strength): Promise<WorkerResponse> {
    return this.call({ type: 'set_strength', strength });
  }

  /** Fire-and-forget best-effort abort of a running search. */
  abortAi(): void {
    this.worker.postMessage({ type: 'abort_ai' });
  }
}

// -- query params (debug/test hooks) ------------------------------------------

interface QueryOptions {
  fast: boolean;
  autostart: 'human_vs_ai' | 'ai_vs_ai' | null;
  humanFirst: boolean;
  /** Explicit query values, or null when absent (storage/default then apply). */
  strength: Strength | null;
  flipped: boolean | null;
  budget?: SearchBudget;
}

function parseQuery(): QueryOptions {
  const params = new URLSearchParams(window.location.search);
  const strength = params.get('strength');
  const budget = params.has('depth')
    ? {
        depth: Number(params.get('depth')),
        soft: Number(params.get('soft') ?? '0.05'),
        hard: Number(params.get('hard') ?? '0.1'),
      }
    : undefined;
  const autostart = params.get('autostart');
  return {
    fast: params.get('fast') === '1',
    autostart:
      autostart === 'human_vs_ai' || autostart === 'ai_vs_ai' ? autostart : null,
    humanFirst: params.get('aifirst') !== '1',
    strength:
      strength === 'easy' || strength === 'medium' || strength === 'hard' ? strength : null,
    flipped: params.has('flip') ? params.get('flip') === '1' : null,
    budget,
  };
}

// -- tiny localStorage helpers (same pattern as sounds.ts) ----------------------

const STORAGE = { flipped: 'jungle.flipped', strength: 'jungle.strength' } as const;

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage unavailable (private mode) — fall back to defaults
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — keep the in-memory value */
  }
}

function storedStrength(): Strength | null {
  const value = readStorage(STORAGE.strength);
  return value === 'easy' || value === 'medium' || value === 'hard' ? value : null;
}

// -- game-over message texts (mirrors the desktop main_window.py) --------------

const WIN_REASONS: Record<string, string> = {
  den: 'entered the enemy den ★',
  elimination: 'captured all enemy pieces',
  no_moves: 'the opponent has no legal moves',
};
const DRAW_REASONS: Record<string, string> = {
  repetition: 'threefold repetition',
  move_limit: '200 plies without a result',
};

function gameOverMessage(gameOver: GameOverInfo, humanSide: 'RED' | 'BLUE' | null): string {
  if (gameOver.winner !== null) {
    const detail = WIN_REASONS[gameOver.reason] ?? gameOver.reason;
    if (humanSide === null) return `${gameOver.winner} wins — ${detail}!`;
    return gameOver.winner === humanSide
      ? `You win! 🎉 (${detail})`
      : `The AI wins — ${detail}.`;
  }
  return `Draw — ${DRAW_REASONS[gameOver.reason] ?? gameOver.reason}.`;
}

// -- the application -------------------------------------------------------------

class App {
  private panels = new Panels();
  private sounds = new SoundPlayer();
  private renderer: BoardRenderer;
  private canvas: HTMLCanvasElement;
  private engine: EngineClient | null = null;
  private about: AboutInfo = { engineVersion: '?', pyodideVersion: '?', sab: false };

  private snapshot: StateSnapshot | null = null;
  private flipped = false;
  private selected: Pos | null = null;
  private targets: Pos[] = [];
  private hover: Pos | null = null;
  private inputEnabled = false;
  private aiThinking = false;
  private generation = 0;
  private animating: BoardView['animating'] = null;
  private cancelAnimation: (() => void) | null = null;
  private aiTimer: ReturnType<typeof setTimeout> | null = null;
  private gameOverKey: string | null = null;
  private query = parseQuery();
  private options: NewGameOptions = {
    mode: 'human_vs_ai',
    humanFirst: true,
    strength: 'medium',
  };
  ready = false;

  constructor() {
    this.canvas = document.getElementById('board') as HTMLCanvasElement;
    this.renderer = new BoardRenderer(this.canvas);
    // Precedence: query param > localStorage > default (false).
    this.flipped = this.query.flipped ?? readStorage(STORAGE.flipped) === '1';
  }

  async start(): Promise<void> {
    try {
      this.wireControls();
      this.wireCanvas();
      this.engine = new EngineClient();
    } catch (err) {
      this.panels.failLoading(String(err));
      return;
    }
    this.engine.onProgress = (stage, detail) => {
      const labels: Record<string, string> = {
        download: 'Downloading Python runtime…',
        python: 'Starting Python…',
        engine: 'Loading the Jungle engine…',
        selftest: 'Verifying engine integrity…',
      };
      this.panels.setLoading(labels[stage] ?? stage, detail);
    };
    this.engine.onSearchInfo = (info) => {
      const sign = info.score >= 0 ? '+' : '';
      this.panels.setAiInfo(
        `d=${info.depth}  ${sign}${(info.score / 100).toFixed(2)}  ${(info.nodes / 1000).toFixed(1)}kN`,
      );
    };
    this.engine.onFatalError = (message) => {
      if (!this.ready) this.panels.failLoading(message);
      else this.panels.setTurn(`Engine error: ${message}`);
    };

    try {
      const ready = await this.engine.init();
      if (ready.type !== 'ready') {
        this.panels.failLoading(
          (ready as { message?: string }).message ?? 'engine initialization failed',
        );
        return;
      }
      this.about = {
        engineVersion: ready.engineVersion,
        pyodideVersion: ready.pyodideVersion,
        sab: ready.sab,
      };
    } catch (err) {
      this.panels.failLoading(String(err));
      return;
    }
    this.panels.hideLoading();
    this.ready = true;
    const initialStrength = this.query.strength ?? storedStrength() ?? 'medium';
    this.panels.setMuted(this.sounds.isMuted);
    this.panels.setFlipped(this.flipped);
    this.panels.setStrength(initialStrength);
    this.resize();
    this.exposeTestHooks();

    this.options = {
      mode: this.query.autostart ?? 'human_vs_ai',
      humanFirst: this.query.humanFirst,
      strength: initialStrength,
    };
    if (this.query.autostart !== null) {
      await this.startNewGame(this.options);
    } else {
      await this.promptNewGame(false);
    }
  }

  // -- controls ------------------------------------------------------------

  private wireControls(): void {
    const on = (id: string, handler: () => void) => {
      document.getElementById(id)?.addEventListener('click', () => {
        this.sounds.unlock();
        handler();
      });
    };
    on('btn-new', () => void this.promptNewGame(true));
    on('btn-undo', () => void this.undo());
    on('btn-flip', () => this.toggleFlip());
    on('btn-full', () => this.toggleFullscreen());
    on('btn-mute', () => this.toggleMute());
    on('btn-howto', () => this.panels.openHowTo());
    on('btn-about', () => this.panels.openAbout(this.about));
    document.getElementById('sel-strength')?.addEventListener('change', (event) => {
      const strength = (event.target as HTMLSelectElement).value as Strength;
      this.options.strength = strength;
      writeStorage(STORAGE.strength, strength);
      void this.engine?.setStrength(strength);
    });
    window.addEventListener('resize', () => this.scheduleResize());
    document.addEventListener('fullscreenchange', () => {
      const active = document.fullscreenElement !== null;
      document.getElementById('btn-full')?.setAttribute('aria-pressed', String(active));
      this.scheduleResize();
    });
  }

  private wireCanvas(): void {
    const area = document.getElementById('board-area');
    if (area) {
      new ResizeObserver(() => this.scheduleResize()).observe(area);
    }
    attachBoardInput(this.canvas, (x, y) => {
      this.sounds.unlock();
      this.onBoardTap(x, y);
    });
    attachBoardHover(
      this.canvas,
      (x, y) => this.onBoardHover(x, y),
      () => this.clearHover(),
    );
  }

  private resizeRaf = 0;

  /** Coalesce the two resize sources (window + ResizeObserver) into one
   *  rAF-driven repaint; ignore hidden/zero-size states. */
  private scheduleResize(): void {
    if (this.resizeRaf !== 0) return;
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = 0;
      this.resize();
    });
  }

  private resize(): void {
    const area = document.getElementById('board-area');
    if (!area || area.clientWidth === 0 || area.clientHeight === 0) return;
    this.renderer.resize(area.clientWidth, area.clientHeight);
    this.render();
  }

  // -- rendering -------------------------------------------------------------

  private render(): void {
    if (!this.snapshot) {
      // No game yet: paint the standard starting position backdrop.
      this.renderer.render({
        cells: STARTING_CELLS,
        flipped: this.flipped,
        selected: null,
        targets: [],
        lastMove: null,
        hover: null,
        animating: null,
      });
      return;
    }
    this.renderer.render({
      cells: this.snapshot.cells,
      flipped: this.flipped,
      selected: this.selected,
      targets: this.targets,
      lastMove: this.snapshot.lastMove,
      hover: this.hover,
      animating: this.animating,
    });
  }

  private updateTurn(): void {
    const snapshot = this.snapshot;
    if (!snapshot) return;
    if (snapshot.gameOver) {
      this.panels.setTurn('Game over');
      return;
    }
    const side = snapshot.sideToMove;
    if (snapshot.humanSide === null) {
      this.panels.setTurn(`AI vs AI — ${side} to move${this.aiThinking ? ' 🤖' : ''}`);
    } else if (side === snapshot.humanSide) {
      this.panels.setTurn(`Your turn (${side})`);
    } else {
      this.panels.setTurn(`AI thinking… (${side})`);
    }
  }

  // -- game flow -------------------------------------------------------------

  private async promptNewGame(allowCancel: boolean): Promise<void> {
    const chosen = await this.panels.openNewGame(this.options, allowCancel);
    if (chosen === null) return;
    await this.startNewGame(chosen);
  }

  private async startNewGame(options: NewGameOptions): Promise<void> {
    if (!this.engine) return;
    this.options = options;
    writeStorage(STORAGE.strength, options.strength);
    this.generation += 1;
    this.clearAiTimer();
    this.cancelAnimation?.();
    this.cancelAnimation = null;
    this.animating = null;
    this.engine.abortAi();
    this.aiThinking = false;
    this.clearSelection();
    this.panels.closeGameOver();
    this.panels.setAiInfo('');
    const state = await this.engine.newGame({
      ...options,
      ...(this.query.budget ? { budget: this.query.budget } : {}),
    });
    if (state.type !== 'state') {
      const message = (state as { message?: string }).message ?? 'unknown error';
      this.panels.setTurn(`Error: ${message}`);
      return;
    }
    this.applySnapshot(state, false);
    this.maybeStartAi();
  }

  private applySnapshot(snapshot: StateSnapshot, animate: boolean): void {
    validateSnapshot(snapshot);
    const lastMove = snapshot.lastMove;
    this.snapshot = snapshot;
    this.clearSelection();
    this.clearHover();
    this.panels.setHistory(snapshot.moveLog);
    this.panels.setUndoEnabled(snapshot.canUndo && !this.aiThinking);
    this.panels.setMode(snapshot.mode === 'ai_vs_ai' ? 'AI vs AI' : 'Human vs AI');
    this.panels.setStrength(snapshot.strength);
    this.updateTurn();

    const toMoveCell = lastMove ? snapshot.cells[cellIndex(lastMove.to[0], lastMove.to[1])] : null;
    if (animate && lastMove && toMoveCell !== null && toMoveCell !== undefined && !this.query.fast) {
      this.runMoveAnimation(lastMove.from, lastMove.to, toMoveCell);
    } else {
      this.animating = null;
      this.render();
    }
    this.syncTestAttributes();
    if (snapshot.gameOver) {
      const key = `${snapshot.moveCount}:${snapshot.gameOver.reason}:${snapshot.gameOver.winner}`;
      if (this.gameOverKey !== key) {
        this.gameOverKey = key;
        this.onGameOver(snapshot.gameOver);
      }
    } else {
      this.gameOverKey = null;
    }
  }

  private runMoveAnimation(from: Pos, to: Pos, cell: number): void {
    this.cancelAnimation?.();
    const geom = this.renderer.geometry;
    const start = cellCenter(from, this.flipped, geom);
    const end = cellCenter(to, this.flipped, geom);
    const lastMove = this.snapshot?.lastMove;
    const isCapture = lastMove !== null && lastMove !== undefined && lastMove.capture
      && lastMove.to[0] === to[0] && lastMove.to[1] === to[1];
    this.animating = { cell, skip: to, center: start };
    this.render();
    this.cancelAnimation = animatePoint(start, end, {
      durationMs: 180,
      onFrame: (center) => {
        this.animating = { cell, skip: to, center };
        this.render();
      },
      onDone: () => {
        if (isCapture) {
          this.runCapturePop(cell, to, end);
        } else {
          this.animating = null;
          this.cancelAnimation = null;
          this.render();
        }
      },
    });
  }

  /** Brief 1.0→1.15→1.0 scale accent after a capturing move lands. */
  private runCapturePop(cell: number, to: Pos, center: { x: number; y: number }): void {
    this.animating = { cell, skip: to, center, scale: 1 };
    this.cancelAnimation = animateTicks({
      durationMs: 150,
      onFrame: (t) => {
        const scale = t < 0.55
          ? 1 + 0.15 * (t / 0.55)
          : 1.15 - 0.15 * ((t - 0.55) / 0.45);
        this.animating = { cell, skip: to, center, scale };
        this.render();
      },
      onDone: () => {
        this.animating = null;
        this.cancelAnimation = null;
        this.render();
      },
    });
  }

  private maybeStartAi(): void {
    const snapshot = this.snapshot;
    if (!snapshot || snapshot.gameOver) {
      this.inputEnabled = false;
      this.updateTurn();
      return;
    }
    const humanTurn = snapshot.humanSide !== null && snapshot.sideToMove === snapshot.humanSide;
    this.inputEnabled = humanTurn && !this.aiThinking;
    this.updateTurn();
    this.render();
    if (humanTurn || this.aiThinking) return;
    this.runAiMove(this.generation);
  }

  private runAiMove(generation: number): void {
    if (!this.engine) return;
    this.aiThinking = true;
    this.inputEnabled = false;
    this.panels.setUndoEnabled(false);
    this.updateTurn();
    void this.engine.requestAiMove().then((response) => {
      if (generation !== this.generation) return; // stale search from an old game
      this.aiThinking = false;
      if (response.type === 'error') {
        this.panels.setTurn(`Engine error: ${response.message}`);
        this.maybeStartAi();
        return;
      }
      if (response.type !== 'ai_move' && response.type !== 'state') return;
      this.applySnapshot(response, response.type === 'ai_move');
      if (!response.gameOver && response.lastMove) {
        if (response.lastMove.capture) this.sounds.capture();
        else this.sounds.move();
      }
      if (response.gameOver) return; // handled by applySnapshot
      if (response.mode === 'ai_vs_ai') {
        this.clearAiTimer();
        this.aiTimer = setTimeout(
          () => this.maybeStartAi(),
          this.query.fast ? 0 : 650,
        );
      } else {
        this.maybeStartAi();
      }
    });
  }

  private onGameOver(gameOver: GameOverInfo): void {
    this.inputEnabled = false;
    this.updateTurn();
    this.render();
    if (gameOver.draw) this.sounds.draw();
    else if (this.snapshot?.humanSide === null) this.sounds.win();
    else if (gameOver.winner === this.snapshot?.humanSide) this.sounds.win();
    else this.sounds.loss();
    void this.panels
      .openGameOver(gameOverMessage(gameOver, this.snapshot?.humanSide ?? null))
      .then((action) => {
        if (action === 'new') void this.promptNewGame(true);
      });
  }

  private clearAiTimer(): void {
    if (this.aiTimer !== null) {
      clearTimeout(this.aiTimer);
      this.aiTimer = null;
    }
  }

  // -- human input -------------------------------------------------------------

  /** Hover glow on own movable pieces; the changed-square gate prevents
   *  pointermove re-render storms. */
  private onBoardHover(x: number, y: number): void {
    const snapshot = this.snapshot;
    let next: Pos | null = null;
    if (this.inputEnabled && snapshot && !snapshot.gameOver) {
      const pos = posFromPoint(x, y, this.flipped, this.renderer.geometry);
      if (pos) {
        const cell = snapshot.cells[cellIndex(pos[0], pos[1])];
        if (cell !== null && cell !== undefined && cellSide(cell) === snapshot.sideToMove) {
          next = pos;
        }
      }
    }
    const changed = next?.[0] !== this.hover?.[0] || next?.[1] !== this.hover?.[1];
    if (!changed) return;
    this.hover = next;
    this.canvas.style.cursor = next ? 'pointer' : '';
    this.render();
  }

  private clearHover(): void {
    if (this.hover === null) return;
    this.hover = null;
    this.canvas.style.cursor = '';
    this.render();
  }

  private onBoardTap(x: number, y: number): void {
    const snapshot = this.snapshot;
    if (!this.inputEnabled || !snapshot || snapshot.gameOver) return;
    const pos = posFromPoint(x, y, this.flipped, this.renderer.geometry);
    if (!pos) return;

    const targetHit = this.targets.find((t) => t[0] === pos[0] && t[1] === pos[1]);
    if (this.selected && targetHit) {
      const from = this.selected;
      this.clearSelection();
      this.render();
      void this.sendHumanMove(from, pos);
      return;
    }
    const cell = snapshot.cells[cellIndex(pos[0], pos[1])];
    if (cell !== null && cell !== undefined && cellSide(cell) === snapshot.sideToMove) {
      this.selected = pos;
      this.targets = snapshot.legalMoves
        .filter(([fr, fc]) => fr === pos[0] && fc === pos[1])
        .map(([, , tr, tc]) => [tr, tc] as Pos);
      this.sounds.select();
    } else {
      this.clearSelection();
    }
    this.render();
  }

  private async sendHumanMove(from: Pos, to: Pos): Promise<void> {
    if (!this.engine) return;
    this.inputEnabled = false;
    const generation = this.generation;
    const response = await this.engine.humanMove(from, to);
    if (generation !== this.generation) return;
    if (response.type === 'move_rejected') {
      this.inputEnabled = true;
      this.render();
      return;
    }
    if (response.type !== 'state') {
      const message = (response as { message?: string }).message ?? 'unknown error';
      this.panels.setTurn(`Engine error: ${message}`);
      return;
    }
    this.applySnapshot(response, true);
    if (!response.gameOver && response.lastMove) {
      if (response.lastMove.capture) this.sounds.capture();
      else this.sounds.move();
    }
    if (!response.gameOver) this.maybeStartAi();
  }

  private clearSelection(): void {
    this.selected = null;
    this.targets = [];
  }

  private async undo(): Promise<void> {
    if (!this.engine || !this.snapshot?.canUndo || this.aiThinking) return;
    this.cancelAnimation?.();
    this.cancelAnimation = null;
    this.animating = null;
    const generation = this.generation;
    const response = await this.engine.undo();
    if (generation !== this.generation) return;
    if (response.type !== 'state') return;
    this.applySnapshot(response, false);
    this.maybeStartAi();
  }

  private toggleFlip(): void {
    // Pure view transform: game state, sides, and turn are untouched.
    this.flipped = !this.flipped;
    writeStorage(STORAGE.flipped, this.flipped ? '1' : '0');
    this.panels.setFlipped(this.flipped);
    this.render();
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement !== null) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void document.documentElement.requestFullscreen().catch(() => undefined);
    }
  }

  private toggleMute(): void {
    this.sounds.setMuted(!this.sounds.isMuted);
    this.panels.setMuted(this.sounds.isMuted);
    if (!this.sounds.isMuted) this.sounds.select();
  }

  // -- test hooks (documented; used by the Playwright suite) ----------------------

  private syncTestAttributes(): void {
    const snapshot = this.snapshot;
    if (!snapshot) return;
    document.body.dataset.sideToMove = snapshot.sideToMove;
    document.body.dataset.moveCount = String(snapshot.moveCount);
    document.body.dataset.gameOver = snapshot.gameOver ? snapshot.gameOver.reason : '';
    document.body.dataset.winner = snapshot.gameOver?.winner ?? '';
  }

  private exposeTestHooks(): void {
    const app = this;
    document.body.dataset.engineReady = '1';
    (window as unknown as { __jungle: unknown }).__jungle = {
      get ready() {
        return app.ready;
      },
      get snapshot() {
        return app.snapshot;
      },
      get flipped() {
        return app.flipped;
      },
      squareCenter(row: number, col: number) {
        const center = cellCenter([row, col], app.flipped, app.renderer.geometry);
        const rect = app.canvas.getBoundingClientRect();
        return { x: rect.left + center.x, y: rect.top + center.y };
      },
    };
  }
}

// Starting-position backdrop painted behind the New Game dialog (geometry
// only; the authoritative layout is jungle/model/constants.py).
const STARTING_CELLS: (number | null)[] = (() => {
  const cells: (number | null)[] = Array(63).fill(null);
  const place = (row: number, col: number, rank: number, side: 0 | 1) => {
    cells[row * 7 + col] = rank | (side << 4);
  };
  const BLUE = 1;
  const RED = 0;
  const blueLayout: [number, number, number][] = [
    [0, 0, 7], [0, 6, 6], [1, 1, 3], [1, 5, 2],
    [2, 0, 1], [2, 2, 5], [2, 4, 4], [2, 6, 8],
  ];
  for (const [row, col, rank] of blueLayout) {
    place(row, col, rank, BLUE);
    place(8 - row, 6 - col, rank, RED);
  }
  return cells;
})();

/** Raw-DOM boot error: works even if the Panels constructor itself threw. */
function showBootError(err: unknown): void {
  const spinner = document.getElementById('loading-spinner');
  const stage = document.getElementById('loading-stage');
  const detail = document.getElementById('loading-detail');
  spinner?.classList.add('failed');
  if (stage) stage.textContent = 'Failed to start the game';
  if (detail) detail.textContent = String(err);
}

try {
  const app = new App();
  app.start().catch(showBootError);
} catch (err) {
  showBootError(err);
}
